/**
 * Step spacing utilities
 * Provides viewport-based step spacing calculation
 */

import { useEffect, useState } from 'react';

/**
 * Step spacing breakpoints based on viewport width
 * These values define the center-to-center distance between steps
 */
const STEP_SPACING_BREAKPOINTS = [
  { min: 375, max: 600, spacing: 124 },
  { min: 600, max: 900, spacing: 212 },
  { min: 900, max: 1200, spacing: 300 },
  { min: 1200, max: 1920, spacing: 412 },
  { min: 1920, max: 2600, spacing: 442 },
] as const;

/**
 * Get step spacing based on viewport width
 * @param viewportWidth - Current viewport width in pixels
 * @returns Step spacing in pixels (center-to-center distance)
 */
export const getStepSpacing = (viewportWidth: number): number => {
  // Find the matching breakpoint
  for (const breakpoint of STEP_SPACING_BREAKPOINTS) {
    if (viewportWidth >= breakpoint.min && viewportWidth < breakpoint.max) {
      return breakpoint.spacing;
    }
  }

  // For viewports wider than 2600px, use the largest spacing
  if (viewportWidth >= 2600) {
    return STEP_SPACING_BREAKPOINTS[STEP_SPACING_BREAKPOINTS.length - 1]
      .spacing;
  }

  // For viewports smaller than 375px, use the smallest spacing
  return STEP_SPACING_BREAKPOINTS[0].spacing;
};

/**
 * Hook to track viewport width and return current step spacing
 * @returns Current step spacing value based on viewport width
 */
export const useStepSpacing = (): number => {
  const [spacing, setSpacing] = useState(() => {
    // Initialize with current viewport width
    if (typeof window !== 'undefined') {
      return getStepSpacing(window.innerWidth);
    }
    return STEP_SPACING_BREAKPOINTS[0].spacing; // Default to smallest spacing
  });

  useEffect(() => {
    // Debounce resize handler
    let timeoutId: NodeJS.Timeout;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const newSpacing = getStepSpacing(window.innerWidth);
        setSpacing(newSpacing);
      }, 100);
    };

    window.addEventListener('resize', handleResize);

    // Initial calculation
    setSpacing(getStepSpacing(window.innerWidth));

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  return spacing;
};
